import { motion, useScroll, useTransform } from 'framer-motion';
import { useState, useRef } from 'react';

// Mock data structure matching your resumeData
const skills = {
  technical: {
    "Languages": ["Java 8", "JavaScript (ES6)", "Python", "Node.js", "GoLang"],
    "AWS Services": ["ECS Clusters", "SNS", "SQS", "Lambda", "API Gateway", "DynamoDB", "RDS"],
    "Backend": ["Spring Boot", "Express", "Flask"],
    "Frontend": ["React.js", "Next.js", "HTML", "CSS", "Bootstrap", "Tailwind"],
    "API Technologies": ["REST (Spring Boot)", "AWS API Gateway", "Postman"],
    "Database": ["MongoDB", "Postgres", "Amazon Aurora (Postgres)"],
    "Architecture": ["Microservices", "Event Sourcing"],
    "DevOps": ["Docker", "ECS", "Kubernetes"],
    "Tools": ["Temporal", "Kafka"],
    "AI/ML": ["Hugging Face", "GCP Vertex AI", "Collab", "Jupyter Notebook"],
    "NLP and OCR": ["Tesseract", "Spacy", "LLama2", "RAG", "ChromaDB", "Gemini Pro"]
  }
};

export default function Skills() {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [0.8, 1]);

  const categories = Object.entries(skills.technical);

  return (
    <section 
    id="skills"
      ref={sectionRef}
      className="min-h-screen py-12 px-6 bg-black relative overflow-hidden"
    >
      {/* Dynamic background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(34, 197, 94, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <motion.div 
        style={{ opacity, scale }}
        className="container mx-auto max-w-7xl relative z-10"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <div className="inline-block mb-6">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "100%" }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="h-px bg-gradient-to-r from-transparent via-green-500 to-transparent mb-6"
            />
            <span className="text-green-400 font-mono text-sm tracking-widest">
              TECHNICAL CAPABILITIES
            </span>
          </div>
          
          <h2 className="text-3xl md:text-6xl font-black mb-6 leading-none">
            <span className="bg-gradient-to-r from-gray-200 via-white to-gray-200 bg-clip-text text-transparent">
              Tech Stack
            </span>
          </h2>
        </motion.div>

        {/* Horizontal scrolling layout */}
        <div className="space-y-16">
          {categories.map(([category, items], categoryIdx) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, x: categoryIdx % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.2, margin: "-100px" }}
              transition={{ duration: 0.7, delay: categoryIdx * 0.1 }}
              className="relative"
            >
              {/* Category section */}
              <div className="flex flex-col md:flex-row md:items-center gap-8 mb-6">
                {/* Category title - left side */}
                <motion.div 
                  className="md:w-64 flex-shrink-0"
                  whileHover={{ x: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    {category}
                  </h3>
                  <motion.div
                    className="h-px bg-gradient-to-r from-green-500/50 to-transparent"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: false, amount: 0.5 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  />
                </motion.div>

                {/* Skills - flowing right */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-3">
                    {items.map((skill, skillIdx) => (
                      <motion.div
                        key={skill}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: false, amount: 0.5 }}
                        transition={{ 
                          duration: 0.4, 
                          delay: categoryIdx * 0.1 + skillIdx * 0.03 
                        }}
                        whileHover={{ 
                          scale: 1.05,
                          y: -2
                        }}
                        onHoverStart={() => setHoveredSkill(skill)}
                        onHoverEnd={() => setHoveredSkill(null)}
                        className="relative group cursor-default"
                      >
                        {/* Skill tag */}
                        <div className="relative px-5 py-2.5 bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-xl overflow-hidden">
                          {/* Hover glow */}
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100"
                            transition={{ duration: 0.3 }}
                          />
                          
                          {/* Animated border */}
                          <motion.div
                            className="absolute inset-0 border border-green-500/0 group-hover:border-green-500/50 rounded-xl"
                            transition={{ duration: 0.3 }}
                          />

                          {/* Text */}
                          <span className="relative text-gray-300 group-hover:text-white font-mono text-sm transition-colors duration-300">
                            {skill}
                          </span>

                          {/* Corner accent */}
                          <motion.div
                            className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100"
                            initial={{ scale: 0, rotate: 0 }}
                            whileHover={{ scale: 1, rotate: 90 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="w-full h-full border-t border-r border-green-500 rounded-tr-xl" />
                          </motion.div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Divider line */}
              {categoryIdx < categories.length - 1 && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: false, amount: 0.5 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent origin-left mt-12"
                />
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}